import { useState } from "react";
import { CHANGELOG } from "../changelog";
import { LOCALE_NAMES, getLocale, setLocale, t, type Locale } from "../i18n";

/**
 * What someone sees when they open the site in an ordinary browser.
 *
 * Kept short on purpose. Whoever lands here followed a link and wants to know
 * what the thing is and how to get it -- the screenshot does more of that work
 * than a paragraph would, and /help is there for anyone who wants detail.
 */
export function Landing({ installUrl }: { installUrl: string }) {
  // The catalogue is read during render, so a redraw is all a switch needs.
  const [, redraw] = useState(0);
  const locale = getLocale();
  const recent = CHANGELOG.slice(0, 3);

  /** The credit line names tarkov.dev, and that name should be a link. */
  const credit = t("landing.footCredit", { tarkovdev: "tarkov.dev" }).split("tarkov.dev");

  return (
    <div className="landing">
      <div className="landing__lang">
        <select
          className="select select--compact"
          value={locale}
          aria-label="Language"
          onChange={(event) => {
            setLocale(event.target.value as Locale);
            redraw((n) => n + 1);
          }}
        >
          {Object.entries(LOCALE_NAMES).map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </div>

      <header className="landing__hero">
        <h1>Tarkov ShareMap</h1>
        {/*
          Japanese has no spaces, so a browser will break this sentence
          anywhere it likes. Splitting it into inline blocks keeps it on one
          line where it fits, and breaks it at the punctuation when it cannot.
        */}
        <p className="landing__tagline">
          <span>{t("landing.tagline1")}</span> <span>{t("landing.tagline2")}</span>
        </p>
        <div className="landing__hero-cta">
          {installUrl && (
            <a className="landing__cta" href={installUrl} target="_blank" rel="noreferrer">
              {t("landing.install")}
            </a>
          )}
          {/*
            The second way in, for "opening Discord first is a chore". It signs
            in with the same Discord account and opens a map straight away, and
            the address bar is then the link to hand to anyone else.
          */}
          <a className="landing__cta landing__cta--quiet" href="/login">
            {t("landing.browser")}
          </a>
          <p>
            {t("landing.installNote")} <a href="/help">{t("sidebar.help")}</a>
          </p>
          <p className="landing__aside">{t("landing.browserNote")}</p>
        </div>
      </header>

      <figure className="landing__figure">
        <img src="/pages/og.png" width={1200} height={630} alt="Tarkov ShareMap" />
      </figure>

      <section className="landing__body">
        <p>{t("landing.body")}</p>
      </section>

      <section className="landing__section">
        <ul className="landing__list">
          <li>{t("landing.point1")}</li>
          <li>{t("landing.point2")}</li>
          <li>{t("landing.point3")}</li>
          <li>{t("landing.point4")}</li>
        </ul>
      </section>

      <section className="landing__section landing__changes">
        <h2>{t("landing.updates")}</h2>
        {recent.map((entry, index) => (
          <div key={`${entry.date}-${index}`} className="landing__change">
            <time dateTime={entry.date}>{entry.date}</time>
            <ul>
              {(locale === "ja" ? entry.ja : entry.en).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ))}
        <p className="landing__aside">
          <a href="/changelog">{t("landing.allUpdates")}</a>
        </p>
      </section>

      <footer className="landing__foot">
        <p>{t("landing.footNote")}</p>
        <p>
          {credit.flatMap((part, index) =>
            index === 0
              ? [part]
              : [
                  <a key={`tk${index}`} href="https://tarkov.dev" target="_blank" rel="noreferrer">
                    tarkov.dev
                  </a>,
                  part,
                ],
          )}
        </p>
        <nav>
          <a href="/help">{t("sidebar.help")}</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </nav>
      </footer>
    </div>
  );
}
